import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Flame, Scale, Play, CheckCircle,
  Thermometer, Clock, Droplets, FileText, Camera, X, Loader2
} from 'lucide-react'
import { getBiocharBatch, updateBiocharBatch, uploadMedia } from '../../services/api'

export default function BatchOperatorForm() {
  const navigate = useNavigate()
  const { batchId } = useParams()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    kiln_type: '',
    biomass_input_kg: '',
    char_output_kg: '',
    moisture_content_pct: '',
    max_temp_celsius: '',
    burn_duration_hours: '',
    process_notes: '',
  })
  const [photos, setPhotos] = useState([])
  const [uploadingPhotos, setUploadingPhotos] = useState(0)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data: batch, isLoading } = useQuery({
    queryKey: ['biocharBatch', batchId],
    queryFn: () => getBiocharBatch(batchId),
    enabled: !!batchId,
  })

  useEffect(() => {
    if (batch) {
      setFormData({
        kiln_type: batch.kiln_type || '',
        biomass_input_kg: batch.biomass_input_kg || '',
        char_output_kg: batch.char_output_kg || '',
        moisture_content_pct: batch.moisture_content_pct || '',
        max_temp_celsius: batch.max_temp_celsius || '',
        burn_duration_hours: batch.burn_duration_hours || '',
        process_notes: batch.process_notes || '',
      })
    }
  }, [batch])

  const updateMutation = useMutation({
    mutationFn: (data) => updateBiocharBatch(batchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['biocharBatch', batchId])
      queryClient.invalidateQueries(['biocharBatches'])
      setSuccess('Batch updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to update batch'),
  })

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue
      setUploadingPhotos(prev => prev + 1)
      try {
        const result = await uploadMedia(file, {
          context: 'biochar_batch',
          ranch_id: batch.ranch_id,
          entity_type: 'biochar_batch',
          entity_id: batchId,
        })
        setPhotos(prev => [...prev, { url: result.url, photoId: result.id, name: file.name }])
      } catch (err) {
        setError(`Failed to upload ${file.name}`)
      } finally {
        setUploadingPhotos(prev => prev - 1)
      }
    }
    e.target.value = ''
  }

  const removePhoto = (index) => setPhotos(prev => prev.filter((_, i) => i !== index))

  const handleStartBatch = () => {
    setError('')
    if (!formData.kiln_type) { setError('Please select a kiln type'); return }
    if (!formData.biomass_input_kg) { setError('Please enter the biomass input weight'); return }
    updateMutation.mutate({
      kiln_type: formData.kiln_type,
      biomass_input_kg: parseFloat(formData.biomass_input_kg),
      status: 'in_progress',
    })
  }

  const handleCompleteBatch = () => {
    setError('')
    if (!formData.char_output_kg) { setError('Please enter the char output weight'); return }
    const payload = { char_output_kg: parseFloat(formData.char_output_kg), status: 'completed' }
    if (formData.moisture_content_pct) payload.moisture_content_pct = parseFloat(formData.moisture_content_pct)
    if (formData.max_temp_celsius) payload.max_temp_celsius = parseFloat(formData.max_temp_celsius)
    if (formData.burn_duration_hours) payload.burn_duration_hours = parseFloat(formData.burn_duration_hours)
    if (formData.process_notes) payload.process_notes = formData.process_notes
    if (photos.length > 0) payload.photo_urls = photos.map(p => p.url)
    updateMutation.mutate(payload)
  }

  if (isLoading) return <div className="max-w-2xl mx-auto p-6"><div className="text-center py-12">Loading...</div></div>
  if (!batch) return <div className="max-w-2xl mx-auto p-6"><div className="text-center py-12 text-red-600">Batch not found</div></div>

  const isAssigned = batch.status === 'assigned'
  const isInProgress = batch.status === 'in_progress'
  const isCompleted = batch.status === 'completed'
  const calculatedYield = formData.biomass_input_kg && formData.char_output_kg
    ? ((parseFloat(formData.char_output_kg) / parseFloat(formData.biomass_input_kg)) * 100).toFixed(1) : null

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isCompleted ? 'View Batch' : 'Process Batch'}</h1>
          <p className="text-gray-600">Batch: {batch.batch_code}</p>
        </div>
      </div>

      <div className="mb-6">
        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
          isAssigned ? 'bg-blue-100 text-blue-800' : isInProgress ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
        }`}>
          {isAssigned && <Play className="h-4 w-4" />}
          {isInProgress && <Flame className="h-4 w-4" />}
          {isCompleted && <CheckCircle className="h-4 w-4" />}
          {batch.status.charAt(0).toUpperCase() + batch.status.slice(1).replace('_', ' ')}
        </span>
      </div>

      {success && <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2"><CheckCircle className="h-5 w-5" />{success}</div>}
      {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>}

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Batch Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-sm text-gray-500">Production Date</p><p className="font-medium">{batch.production_date ? new Date(batch.production_date).toLocaleDateString() : 'Not set'}</p></div>
          <div><p className="text-sm text-gray-500">Source</p><p className="font-medium">{batch.source_description || 'Lantana patches'}</p></div>
        </div>
      </div>

      {isAssigned && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center"><span className="text-blue-700 font-bold">1</span></div>
            <h2 className="text-lg font-semibold text-gray-900">Start Batch</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kiln Type *</label>
              <select value={formData.kiln_type} onChange={(e) => setFormData({ ...formData, kiln_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                <option value="">Select kiln type</option>
                <option value="kon_tiki">Kon-Tiki (Open Flame)</option>
                <option value="tlud">TLUD (Top-Lit Updraft)</option>
                <option value="retort">Retort Kiln</option>
                <option value="pit">Pit Kiln</option>
                <option value="drum">Drum Kiln</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><Scale className="h-4 w-4 inline mr-1" />Biomass Input Weight (kg) *</label>
              <input type="number" step="0.1" min="0" value={formData.biomass_input_kg} onChange={(e) => setFormData({ ...formData, biomass_input_kg: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Enter weight of biomass going into kiln" />
            </div>
            <button onClick={handleStartBatch} disabled={updateMutation.isPending} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Play className="h-5 w-5" />{updateMutation.isPending ? 'Starting...' : 'Start Batch'}
            </button>
          </div>
        </div>
      )}

      {isInProgress && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center"><span className="text-green-700 font-bold">2</span></div>
            <h2 className="text-lg font-semibold text-gray-900">Complete Batch</h2>
          </div>
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600"><strong>Kiln:</strong> {batch.kiln_type?.replace('_', ' ') || 'Not recorded'}</p>
            <p className="text-sm text-gray-600"><strong>Biomass Input:</strong> {batch.biomass_input_kg} kg</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><Scale className="h-4 w-4 inline mr-1" />Char Output Weight (kg) *</label>
              <input type="number" step="0.1" min="0" value={formData.char_output_kg} onChange={(e) => setFormData({ ...formData, char_output_kg: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Enter weight of biochar produced" />
            </div>
            {calculatedYield && (
              <div className={`p-3 rounded-lg ${parseFloat(calculatedYield) < 18 ? 'bg-red-50 border border-red-200' : parseFloat(calculatedYield) > 40 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <p className={`text-sm font-medium ${parseFloat(calculatedYield) < 18 ? 'text-red-800' : parseFloat(calculatedYield) > 40 ? 'text-yellow-800' : 'text-green-800'}`}>
                  Calculated Yield: {calculatedYield}%{parseFloat(calculatedYield) < 18 && ' ⚠️ Low yield'}{parseFloat(calculatedYield) > 40 && ' ⚠️ Unusually high - please verify'}
                </p>
              </div>
            )}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Optional Process Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Droplets className="h-4 w-4 inline mr-1" />Moisture Content (%)</label>
                  <input type="number" step="0.1" min="0" max="100" value={formData.moisture_content_pct} onChange={(e) => setFormData({ ...formData, moisture_content_pct: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="e.g., 15" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Thermometer className="h-4 w-4 inline mr-1" />Max Temperature (°C)</label>
                  <input type="number" step="1" min="0" value={formData.max_temp_celsius} onChange={(e) => setFormData({ ...formData, max_temp_celsius: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="e.g., 450" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1"><Clock className="h-4 w-4 inline mr-1" />Burn Duration (hours)</label>
                  <input type="number" step="0.5" min="0" value={formData.burn_duration_hours} onChange={(e) => setFormData({ ...formData, burn_duration_hours: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="e.g., 4.5" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1"><FileText className="h-4 w-4 inline mr-1" />Process Notes</label>
                  <textarea value={formData.process_notes} onChange={(e) => setFormData({ ...formData, process_notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" rows={3} placeholder="Any observations about the burn process..." />
                </div>
              </div>
            </div>
            <div className="pt-4 border-t">
              <h3 className="text-sm font-medium text-gray-700 mb-3"><Camera className="h-4 w-4 inline mr-1" />Batch Photos</h3>
              <p className="text-xs text-gray-500 mb-3">Take photos of the finished biochar for your records</p>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-3">
                  {photos.map((photo, i) => (
                    <div key={i} className="relative group">
                      <img src={photo.url} alt={photo.name} className="w-full h-24 object-cover rounded-lg" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {uploadingPhotos > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <Loader2 className="h-4 w-4 animate-spin" />Uploading {uploadingPhotos} photo{uploadingPhotos > 1 ? 's' : ''}...
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm">
                <Camera className="h-4 w-4" />Add Photos
                <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
              </label>
            </div>
            <button onClick={handleCompleteBatch} disabled={updateMutation.isPending || uploadingPhotos > 0} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              <CheckCircle className="h-5 w-5" />{updateMutation.isPending ? 'Completing...' : 'Complete Batch'}
            </button>
          </div>
        </div>
      )}

      {isCompleted && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Batch Summary</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">Kiln Type</p><p className="font-medium capitalize">{batch.kiln_type?.replace('_', ' ') || '-'}</p></div>
              <div><p className="text-sm text-gray-500">Biomass Input</p><p className="font-medium">{batch.biomass_input_kg} kg</p></div>
              <div><p className="text-sm text-gray-500">Char Output</p><p className="font-medium">{batch.char_output_kg} kg</p></div>
              <div><p className="text-sm text-gray-500">Yield</p><p className={`font-medium ${batch.yield_pct < 18 ? 'text-red-600' : batch.yield_pct > 40 ? 'text-yellow-600' : 'text-green-600'}`}>{batch.yield_pct}%</p></div>
            </div>
            {(batch.moisture_content_pct || batch.max_temp_celsius || batch.burn_duration_hours) && (
              <div className="pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Process Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  {batch.moisture_content_pct && <div><p className="text-sm text-gray-500">Moisture</p><p className="font-medium">{batch.moisture_content_pct}%</p></div>}
                  {batch.max_temp_celsius && <div><p className="text-sm text-gray-500">Max Temp</p><p className="font-medium">{batch.max_temp_celsius}°C</p></div>}
                  {batch.burn_duration_hours && <div><p className="text-sm text-gray-500">Duration</p><p className="font-medium">{batch.burn_duration_hours} hrs</p></div>}
                </div>
              </div>
            )}
            {batch.process_notes && <div className="pt-4 border-t"><p className="text-sm text-gray-500">Process Notes</p><p className="text-sm text-gray-700 mt-1">{batch.process_notes}</p></div>}
            {batch.photo_urls?.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2"><Camera className="h-4 w-4 inline mr-1" />Batch Photos</p>
                <div className="grid grid-cols-3 gap-3">
                  {batch.photo_urls.map((url, i) => (
                    <img key={i} src={url} alt={`Batch photo ${i + 1}`} className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80" onClick={() => window.open(url, '_blank')} />
                  ))}
                </div>
              </div>
            )}
            <div className="p-4 bg-green-50 rounded-lg"><p className="text-sm text-green-800">✓ Batch completed. {batch.quantity_remaining_kg} kg available for application or sale.</p></div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={() => navigate('/biochar')} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Back to Biochar Dashboard</button>
      </div>
    </div>
  )
}
